import { Component, HostListener, Input } from "@angular/core";
import { Helper } from "src/app/core/helpers/helper";
import { CategoryGroup, GridChildViewConfig, TextConfig } from "../../models/category-group.model";
import { CommonModule } from "@angular/common";
import { ProductComponent } from "../product/product.component";
import { TranslateModule } from "@ngx-translate/core";
import { MainCategory } from "../../models/main-category.model";
import { ActivatedRoute } from "@angular/router";

@Component({
  standalone: true,
  imports: [CommonModule, ProductComponent, TranslateModule],
  selector: "app-product-group",
  templateUrl: "./product-group.component.html",
  styleUrl: "./product-group.component.scss"
})
export class ProductGroupComponent {
  isDesktop: boolean = window.innerWidth >= 1024;
  @Input() categoryGroup!: CategoryGroup;
  @Input() merchantId: string = "";
  @Input() isCart!: boolean;
  @Input() serviceMainCategory!: MainCategory;
  @Input() isBrandType!: boolean;
  @Input() serviceMerchantId: string = "";

  constructor(public helper: Helper, private route: ActivatedRoute) {}

  @HostListener("window:resize", ["$event"])
  /**
   * Host listener for window resize events.
   * Updates the `isDesktop` property based on the window width.
   * Sets `isDesktop` to true if the window width is greater than or equal to 1024, otherwise false.
   * @param event The resize event.
   */
  onResize(): void {
    this.isDesktop = window.innerWidth >= 1024;
  }

  /**
   * Get the CSS styles for the product container.
   * @param categoryGroup The category group for which to generate the styles.
   * @returns An object with CSS styles including margin, background color, border,
   * border radius, and padding based on the grid view configuration.
   */
  getProductContainerStyle(categoryGroup: CategoryGroup): { [key: string]: string } {
    const grid = categoryGroup.gridViewConfig;
    const styles: { [key: string]: string } = {
      margin: `${grid.padding[0]}px ${grid.padding[1]}px ${grid.padding[2]}px ${grid.padding[3]}px`,
      backgroundColor: grid.bgColor,
      border: `${grid.border.size}px solid ${grid.border.color}`,
      borderRadius: `${grid.border.radius[0]}px ${grid.border.radius[1]}px ${grid.border.radius[2]}px ${grid.border.radius[3]}px`,
      padding: `${grid.childPadding[0]}px ${grid.childPadding[1]}px ${grid.childPadding[2]}px ${grid.childPadding[3]}px`
    };
    return styles;
  }

  /**
   * Get the CSS styles for the grid layout of the products.
   * @param categoryGroup The category group for which to generate the styles.
   * @returns An object with CSS styles for the grid layout.
   */
  getProductGridLayoutStyle(categoryGroup: CategoryGroup): { [key: string]: string } {
    const grid = categoryGroup.gridViewConfig;
    const styles: { [key: string]: string } = {
      display: "grid",
      gridTemplateColumns: `repeat(${grid.maxColumnCount}, 1fr)`,
      gap: `${grid.betweenPadding}px`
    };
    return styles;
  }

  /**
   * Returns the style object for the title text.
   * The style includes color, font size, font weight, padding, and text alignment
   * based on the title configuration.
   * @param titleConfig The title configuration object.
   * @returns An object with CSS styles for the title text.
   */
  getTitleStyle(titleConfig: TextConfig): { [key: string]: string | number } {
    return {
      color: titleConfig.color,
      fontSize: `${titleConfig.fontSize}px`,
      fontWeight: titleConfig.fontWeight,
      padding: `${titleConfig.padding[0]}px ${titleConfig.padding[1]}px ${titleConfig.padding[2]}px ${titleConfig.padding[3]}px`,
      textAlign: titleConfig.position[2].toLowerCase()
    };
  }

  /**
   * Navigates to the appropriate product list page to view all items in the given category group.
   * Updates the `merchantId` property based on query parameters if present.
   * If the current route does not include "/users/create-booking/e-delivery", navigates to the quick commerce products page.
   * Otherwise, if `merchantId` is available, navigates to the e-delivery product list page.
   * Passes the category group's ID, title, and merchant ID as query parameters.
   *
   * @param categoryGroup The category group to be viewed in the product list.
   */
  viewAll(categoryGroup: CategoryGroup): void {
    this.route.queryParams.subscribe((param) => {
      if (param["merchantId"]) {
        this.merchantId = param["merchantId"];
      }
    });
    const customConfig = {
      gridChildViewConfig: categoryGroup.gridChildViewConfig,
      gridViewConfig: categoryGroup.gridViewConfig,
      isSetCustomDesign: true
    };
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      this.helper.route.navigate(["/users/create-booking/quick-commerce/products"], {
        queryParams: {
          groupId: categoryGroup._id,
          name: categoryGroup.headerFooterViewConfig.header.title.text,
          merchantId: this.merchantId,
          customConfig: JSON.stringify(customConfig)
        }
      });
    } else if (this.helper.route.url.includes("/users/create-booking/services") && this.serviceMainCategory && this.serviceMainCategory._id) {
      this.helper.route.navigate(["/users/create-booking/services/product-list"], {
        queryParams: {
          groupId: categoryGroup._id,
          name: categoryGroup.headerFooterViewConfig.header.title.text,
          serviceMainCategory: JSON.stringify(this.serviceMainCategory),
          customConfig: JSON.stringify(customConfig)
        }
      });
    } else if (this.helper.route.url.includes("/users/create-booking/e-delivery") &&this.merchantId) {
      this.helper.route.navigate(["/users/create-booking/e-delivery/product-list"], {
        queryParams: {
          groupId: categoryGroup._id,
          name: categoryGroup.headerFooterViewConfig.header.title.text,
          merchantId: this.merchantId,
          customConfig: JSON.stringify(customConfig)
        }
      });
    }
  }

  /**
   * Determines if the layout for the given category group is horizontal.
   * A layout is considered horizontal if the title position is either "RIGHT" or "LEFT".
   *
   * @param categoryGroup The category group whose layout is to be checked.
   * @returns true if the layout is horizontal, false otherwise.
   */
  isHorizontalLayout(categoryGroup: CategoryGroup): boolean {
    return categoryGroup.gridChildViewConfig.title.position[1] === "RIGHT" || categoryGroup.gridChildViewConfig.title.position[1] === "LEFT";
  }

  /**
   * Returns the CSS style object for a button based on its configuration.
   * @param buttonConfig The button configuration object.
   * @returns An object with CSS styles for the button, including background color and color.
   */
  getButtonStyle(childConfig: GridChildViewConfig): { [key: string]: string } {
    const style: { [key: string]: string } = {};
    if (childConfig && childConfig.button) {
      (style["background-color"] = childConfig?.button?.bgColor), (style["color"] = childConfig?.button?.color);
    } else {
      style["background-color"] = childConfig.bgColor;
    }
    return style;
  }
}
